import {
  AcquisitionReport,
  AcquisitionWindow
} from "@/interfaces/OfficeAcquisition.interfaces";

function dateOnly(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
    throw new Error("Choose valid UTC dates.");
  const time = Date.parse(value + "T00:00:00Z");
  if (
    !Number.isFinite(time) ||
    new Date(time).toISOString().slice(0, 10) !== value
  ) {
    throw new Error("Choose valid UTC dates.");
  }
  return time;
}

export function reportWindow(
  from: string,
  through: string,
  asOf: string,
  model: string,
  basis: string
): AcquisitionWindow {
  const start = dateOnly(from),
    last = dateOnly(through),
    end = last + 86400000;
  const cutoff =
    basis === "legacy"
      ? Date.now()
      : /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(asOf)
      ? Date.parse(asOf + ":00Z")
      : NaN;
  if (
    last < start ||
    end - start > 366 * 86400000 ||
    !Number.isFinite(cutoff) ||
    cutoff > Date.now() ||
    (basis !== "legacy" &&
      new Date(cutoff).toISOString().slice(0, 16) !== asOf) ||
    cutoff < start
  ) {
    throw new Error(
      "Choose a window of at most 366 days and an as-of time between its start and now (UTC)."
    );
  }
  return {
    from: new Date(start).toISOString(),
    to: new Date(end).toISOString(),
    asOf: new Date(cutoff).toISOString(),
    model,
    basis
  };
}

export function utcLabel(value: string | null | undefined): string {
  if (!value) return "Unknown";
  // MySQL date strings have no zone; this report's contract is UTC.
  const normalized =
    value.indexOf("T") === -1 ? value.replace(" ", "T") + "Z" : value;
  const time = Date.parse(normalized);
  return Number.isFinite(time)
    ? new Date(time)
        .toISOString()
        .replace("T", " ")
        .replace(/\.\d{3}Z$/, " UTC")
    : "Unknown";
}

export function minorMoney(
  value: string | number | null | undefined,
  currency: string
): string {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "number" && !Number.isSafeInteger(value))
  )
    return "Unavailable";
  const raw = String(value);
  if (!/^-?\d+$/.test(raw)) return "Unavailable";
  if (!["CAD", "USD"].includes(currency))
    return `${raw} minor units (${currency})`;
  const sign = raw.charAt(0) === "-" ? "−" : "";
  const digits = raw
    .replace(/^-/, "")
    .replace(/^0+(?=\d)/, "")
    .padStart(3, "0");
  return `${currency} ${sign}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

export function officeLabel(report: AcquisitionReport, id: number): string {
  const item = report.locationLabels.find(
    row => Number(row.locationId) === Number(id)
  );
  if (!item) return `Location ${id} (details unavailable)`;
  return [item.practiceName || `Location ${id}`, item.city, item.province]
    .filter(Boolean)
    .join(" · ");
}

function csvCell(value: unknown): string {
  const text =
    value === null || value === undefined ? "Unknown" : String(value);
  // Campaign names are external input; prevent spreadsheet formula execution.
  const safe =
    /^[\s]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text) ? "'" + text : text;
  return '"' + safe.replace(/"/g, '""') + '"';
}

export function cohortCsv(report: AcquisitionReport): string {
  const common = [
    report.version,
    report.environment,
    report.basis,
    report.model || "Not applicable",
    report.from,
    report.to,
    report.basis === "legacy_location_created"
      ? "Not applicable to historical creation baseline"
      : report.asOf
  ];
  const headings = [
    "Version",
    "Environment",
    "Basis",
    "Model",
    "From UTC inclusive",
    "To UTC exclusive",
    "As of UTC",
    "Office",
    "Location ID",
    "Customer ID",
    "Entry UTC",
    "Source",
    "Campaign",
    "Campaign ID",
    "Ad set ID",
    "Ad ID",
    "Seven-day mature",
    "Posted within seven days",
    "First post UTC",
    "First completion UTC",
    "First-completion feedback",
    "Repeat use",
    "Expansion locations",
    "Money coverage",
    "Fixture and churn coverage"
  ];
  const rows: unknown[][] =
    report.basis === "legacy_location_created"
      ? (report.rows || []).map(row => [
          ...common,
          officeLabel(report, row.locationId),
          row.locationId,
          row.dentistId,
          utcLabel(row.createdOn),
          row.utmSource,
          row.utmCampaign,
          null,
          null,
          null,
          "Not applicable",
          "Not applicable",
          null,
          null,
          null,
          null,
          row.priorLocations
            ? "Existing customer's later location"
            : "Unknown historical eligibility",
          "Unavailable",
          [
            row.isTest ? "Test" : "Test status not flagged",
            row.exclusionReason,
            row.removed || row.deactivated ? "Removed / deactivated" : "Active"
          ]
            .filter(Boolean)
            .join("; ")
        ])
      : (report.included || []).map(row => [
          ...common,
          officeLabel(report, row.locationId),
          row.locationId,
          row.dentistId,
          utcLabel(row.completedOn),
          row.acquisition.rawSource,
          row.acquisition.campaign,
          row.acquisition.campaignId,
          row.acquisition.adsetId,
          row.acquisition.adId,
          row.sevenDayMature,
          row.postedWithinSevenDays === null
            ? "Not yet mature"
            : row.postedWithinSevenDays,
          utcLabel(row.firstPostOn),
          utcLabel(row.firstCompletedOn),
          row.firstCompletionFeedback,
          row.repeatCompletedJobs,
          row.expansionLocationIds.join("; "),
          "Net fees unavailable; Finance verification pending",
          "Eligible frozen registration; later churn retained"
        ]);
  return [headings, ...rows]
    .map(row => row.map(csvCell).join(","))
    .join("\r\n");
}
